import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Info, Plus, Trash2, X } from "lucide-react";
import { useFreelancerStore } from "@/store/freelancerStore";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { dateFormat } from "@/lib/utils";

interface Project {
  name: string;
  description: string;
  technologiesUsed: string[];
  role: string;
  projectDuration: {
    start: string;
    end: string;
  };
}
interface ProfileProjectsFormProps {
  formData?: {
    project: Project[];
    _id: string;
  };
  onSave: (projects: Project[]) => void;
}

const emptyProject: Project = {
  name: "",
  description: "",
  technologiesUsed: [],
  role: "",
  projectDuration: {
    start: "",
    end: "",
  },
};

const ProfileProjectsForm = ({
  formData = { project: [emptyProject], _id: "" },
  onSave,
}: ProfileProjectsFormProps) => {
  const { profile, updateFreelancerProfile } = useFreelancerStore();
  const { user } = useUserStore();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>(
    formData?.project?.length ? formData?.project : [emptyProject]
  );

  const [technologies, setTechnologies] = useState(
    profile?.project?.technologiesUsed || []
  );
  const [techInput, setTechInput] = useState("");
  const [techInputs, setTechInputs] = useState<string[]>(
    projects.map(() => "")
  );

  const handleAddTech = (index: number) => {
    const input = techInputs[index];
    if (!input) return;

    const newTechs = input
      .split(",")
      .map((tech) => tech.trim())
      .filter(
        (tech) =>
          tech.length > 0 && !projects[index].technologiesUsed.includes(tech)
      );

    if (newTechs.length > 0) {
      const updated = [...projects];
      updated[index].technologiesUsed = [
        ...updated[index].technologiesUsed,
        ...newTechs,
      ];
      setProjects(updated);

      const updatedInputs = [...techInputs];
      updatedInputs[index] = "";
      setTechInputs(updatedInputs);
    }
  };

  const handleRemoveTech = (tech: string, index: number) => {
    const updated = [...projects];
    updated[index].technologiesUsed = updated[index].technologiesUsed.filter(
      (t) => t !== tech
    );
    setProjects(updated);
  };

  const handleChange = (index: number, field: keyof Project, value: any) => {
    const updated = [...projects];
    (updated[index] as any)[field] = value;
    setProjects(updated);
  };

  const handleDateChange = (
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    const updated = [...projects];
    updated[index].projectDuration[field] = value;
    setProjects(updated);
  };

  const addProject = () => setProjects([...projects, { ...emptyProject }]);

  const removeProject = (index: number) => {
    const updated = [...projects];
    updated.splice(index, 1);
    setProjects(updated);
  };

  const handleSubmit = async () => {
    try {
      await updateFreelancerProfile(
        formData?._id,
        { project: projects },
        user?.accessToken
      );
      setProjects([emptyProject]);
      onSave(projects);
      toast({
        title: "Project updated successfully",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update project. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    setTechInputs(projects.map(() => ""));
  }, [projects]);

  return (
    <div className="my-4 mx-4">
      {projects.map((project, index) => (
        <div
          key={index}
          className="border p-4 rounded-md bg-white relative space-y-3 my-1"
        >
          <div className="mt-4">
            <Label>Project Name</Label>
            <Input
              value={project.name}
              onChange={(e) => handleChange(index, "name", e.target.value)}
              placeholder="e.g., Amazon Clone"
            />
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={project.description}
              onChange={(e) =>
                handleChange(index, "description", e.target.value)
              }
              placeholder="Project details..."
            />
          </div>

          <div className="flex-1  p-3 rounded-lg border border-blue-100 shadow-sm">
            <h4 className="text-sm font-medium mb-2 text-blue-700">
              Technologies Used
            </h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="relative group ml-2">
                  <Info className="h-4 w-4 text-gray-400" />
                  <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
                    Add technologies used in this project (e.g., React, Python).
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., React, Python"
                  value={techInputs[index] || ""}
                  onChange={(e) => {
                    const updatedInputs = [...techInputs];
                    updatedInputs[index] = e.target.value;
                    setTechInputs(updatedInputs);
                  }}
                  onKeyPress={(e) => e.key === "Enter" && handleAddTech(index)}
                />
                <Button type="button" onClick={() => handleAddTech(index)}>
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {project.technologiesUsed.map((tech) => (
                  <Badge
                    key={tech}
                    variant="secondary"
                    className="bg-sky-100 text-sky-700 flex items-center gap-1"
                  >
                    {tech}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => handleRemoveTech(tech, index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label>Role</Label>
            <Input
              value={project.role}
              onChange={(e) => handleChange(index, "role", e.target.value)}
              placeholder="e.g., Backend Developer"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateFormat(project.projectDuration.start)}
                onChange={(e) =>
                  handleDateChange(index, "start", e.target.value)
                }
              />
            </div>
            <div className="flex-1">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateFormat(project.projectDuration.end)}
                onChange={(e) => handleDateChange(index, "end", e.target.value)}
              />
            </div>
          </div>

          {projects.length > 1 && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-0 right-2"
              onClick={() => removeProject(index)}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      ))}

      <div className="flex gap-4 my-2">
        <Button
          type="button"
          onClick={addProject}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus />
        </Button>

        <Button
          type="button"
          onClick={handleSubmit}
        //   className="bg-indigo-600 hover:bg-indigo-700"
        >
          Save Projects
        </Button>
      </div>
    </div>
  );
};

export default ProfileProjectsForm;
